import { useState, useEffect, useRef, ReactNode, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Order } from "../types/order";
import { mockOrders } from "../data/mockOrders";
import { OrderCard } from "./OrderCard";
import { OrderListItem } from "./OrderListItem";
import { TipCard } from "./TipCard";
import { TipCardListWrapper } from "./TipCardListWrapper";
import { UnifiedTipCard } from "./UnifiedTipCard";
import { EmptyStateFilters } from "./EmptyStateFilters";
import { SectionHeader, SectionType } from "./SectionHeader";
import { SectionTooltip } from "./SectionTooltip";
import { TipCardConfig, TipCardState } from "../types/tipcard";
import { useTipCardPlacement } from "../hooks/useTipCardPlacement";
import { OffersService } from "@/services/store/offers";
import {
  matchListToOrders,
  matchOfferToOrder,
  offersListToOrders,
} from "@/utils/store/orderConverter";
import { IOffer } from "@/services/store/interfaces/offers.interfaces";
import { off } from "process";
import { LoadingScreen } from "./Loading";
import { UserService } from "@/services/store/user";
import { OrderFeedSkeleton } from "./OrderFeedSkeleton";
import { LoadingMoreSkeleton } from "./LoadingMoreSkeleton";
import { ErrorScreen, ErrorType } from "./ErrorScreen";
import { AxiosError } from "axios";
import { CmsTooltipSection, ICmsTooltip } from "@/services/store/cmsTooltips";

interface FilterState {
  urgente: boolean;
  naoUrgente: boolean;
  presencial: boolean;
  remoto: boolean;
  ninguemLiberou: boolean;
  outrosLiberaram: boolean;
  naoAvaliados: boolean;
  gostei: boolean;
  naoGostei: boolean;
}

interface OrderFeedProps {
  userRadius?: number;
  onSelectOrder: (order: Order) => void;
  viewMode?: "cards" | "list";
  sortOption?: string;
  filters?: FilterState;
  onClearFilters?: () => void;
  dismissedTips?: Set<string>;
  onDismissTip?: (tipId: string) => void;
  tipCardConfigs?: TipCardConfig[];
  tipCardState?: TipCardState;
  orderActions?: Record<
    string,
    "like" | "dislike" | "dislike-removed" | "report" | null
  >;
  ordersDisabled?: boolean;
  onCurrentSectionChange?: (section: SectionType | null) => void;
  topBarHeight?: number;
  authToken: string;
  offerList: IOffer[];
  profileId: number | undefined;
  reloadTrigger?: number;
  /**
   * IDs de pedidos que o profissional acabou de comprar nesta sessão.
   * São removidos do feed em tempo de execução (sem recarregar a lista),
   * preservando a posição de rolagem. No próximo reload orgânico o backend
   * já não retorna esses pedidos.
   */
  boughtOrderIds?: string[];
  /**
   * Ids de pedidos rejeitados ("não gostei"). Removidos da listagem atual
   * preservando a posição de rolagem. No próximo reload orgânico o backend
   * já não retorna esses pedidos.
   */
  removedOrderIds?: string[];
  /**
   * Chamado quando uma atualização disparada por `reloadTrigger` termina
   * (sucesso ou erro). Usado pelo pull-to-refresh para encerrar o spinner
   * apenas quando os dados realmente chegam.
   */
  onReloadComplete?: () => void;
  /**
   * Reporta qual é a primeira seção preenchida do feed, na ordem fixa
   * express → para-voce → outros. Usado para colorir o indicador de
   * pull-to-refresh com a cor da seção exibida no topo.
   */
  onFirstSectionChange?: (section: SectionType | null) => void;
  /**
   * Tooltips vindos do CMS, agrupados por seção.
   * Se estiver vazio ou não houver tooltips para uma seção,
   * nenhuma dica é exibida naquela lista.
   */
  cmsTooltipsBySection?: Partial<Record<CmsTooltipSection, ICmsTooltip[]>>;
}

export function OrderFeed({
  userRadius,
  onSelectOrder,
  viewMode = "cards",
  sortOption = "recommended",
  filters,
  onClearFilters,
  dismissedTips,
  onDismissTip,
  tipCardConfigs = [],
  tipCardState = {},
  orderActions = {},
  ordersDisabled = false,
  onCurrentSectionChange,
  topBarHeight = 60,
  authToken,
  offerList,
  profileId,
  reloadTrigger = 0,
  onReloadComplete,
  onFirstSectionChange,
  cmsTooltipsBySection,
  boughtOrderIds = [],
  removedOrderIds = [],
}: OrderFeedProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [matchOrders, setMatchOrders] = useState<Order[]>([]);
  // Inicia como `true` para exibir o skeleton imediatamente no primeiro render,
  // antes mesmo de o `userRadius` chegar e disparar a primeira busca. Sem isso,
  // a lista vazia não tem altura e o fundo escuro do container aparece atrás
  // (tela preta) até o skeleton entrar.
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [loadingMatchOffers, setLoadingMatchOffers] = useState(false);
  const [currentSection, setCurrentSection] = useState<SectionType | null>(
    null,
  );
  const [lastOffersLength, setLastOffersLength] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [infiniteScrollOffset, setInfiniteScrollOffset] = useState(0);
  const lastScrollTimeRef = useRef<number>(0);
  const [errorType, setErrorType] = useState<ErrorType | null>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Refs for each section container
  const expressRef = useRef<HTMLDivElement>(null);
  const paraVoceRef = useRef<HTMLDivElement>(null);
  const outrosRef = useRef<HTMLDivElement>(null);

  // Refs for each section HEADER (title element)
  const expressHeaderRef = useRef<HTMLDivElement>(null);
  const paraVoceHeaderRef = useRef<HTMLDivElement>(null);
  const outrosHeaderRef = useRef<HTMLDivElement>(null);

  const handleGetOffers = useCallback(
    async (authToken: string, radius: number, offset: number = 0) => {
      if (offset === 0) {
        setLoadingOffers(true);
        setErrorType(null);
      } else {
        setIsLoadingMore(true);
      }
      await OffersService.getOffers({ authToken, limit: 30, radius, offset })
        .then((res) => {
          const parsed = offersListToOrders(res.offers.offers_list);
          setLastOffersLength(res.offers.offers_list.length);
          if (offset === 0) {
            setLoadingOffers(false);
            setHasInitialLoad(true);
          } else {
            setIsLoadingMore(false);
          }
          setOrders((prev) => [...prev, ...parsed]);
          setErrorType(null);
        })
        .catch((error) => {
          console.error(error);
          if (offset === 0) {
            setLoadingOffers(false);
            setHasInitialLoad(true);

            // Determine error type based on error response
            if (error instanceof AxiosError) {
              if (
                error.code === "ECONNABORTED" ||
                error.code === "ERR_NETWORK" ||
                !error.response
              ) {
                setErrorType("no-connection");
              } else if (
                error.response?.status === 500 ||
                error.response?.status >= 500
              ) {
                setErrorType("server-error");
              } else {
                setErrorType("generic");
              }
            } else {
              setErrorType("generic");
            }
          } else {
            setIsLoadingMore(false);
          }
        });
    },
    [],
  );

  const handleGetMatchOffers = useCallback(async () => {
    if (!profileId) return;
    setLoadingMatchOffers(true);

    await OffersService.getMatchOffers({ profileId: profileId, limit: 50 })
      .then((res) => {
        const parsed = matchListToOrders(res);
        setLoadingMatchOffers(false);
        setMatchOrders(parsed);
        setOrders((prev) => [...prev, ...parsed]);
      })
      .catch((error) => {
        console.error(error);
        // Em caso de erro nesta lista, apenas não exibimos os matches
        // e seguimos mostrando as demais listas normalmente.
        setLoadingMatchOffers(false);
      });
  }, [profileId]);

  useEffect(() => {
    if (offerList.length > 0) {
      const parsed = offersListToOrders(offerList);
      if (reloadTrigger === 0) {
        setOrders([...matchOrders, ...parsed]);
        setLastOffersLength(offerList.length);
        setHasInitialLoad(true);
        setErrorType(null);
      }
    }
  }, [offerList, reloadTrigger, matchOrders]);

  useEffect(() => {
    if (userRadius) {
      void handleGetOffers(authToken, userRadius);
      void handleGetMatchOffers();
    }
  }, [userRadius]);

  useEffect(() => {
    if (reloadTrigger > 0) {
      // Sem raio definido não há o que recarregar; encerra o pull-to-refresh
      // para não deixar o spinner preso.
      if (!userRadius) {
        onReloadComplete?.();
        return;
      }

      setOrders([]);
      setMatchOrders([]);
      setLastOffersLength(0);
      setInfiniteScrollOffset(0);
      setErrorType(null);
      setHasInitialLoad(false);

      void (async () => {
        try {
          await Promise.all([
            handleGetOffers(authToken, userRadius),
            handleGetMatchOffers(),
          ]);
        } finally {
          // Notifica o pull-to-refresh que a atualização terminou,
          // independentemente de sucesso ou erro.
          onReloadComplete?.();
        }
      })();
    }
  }, [reloadTrigger]);

  const handleMarkAsViewed = (orderId: string) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, isViewed: true } : order,
      ),
    );
  };

  const handleSelectOrder = (order: Order) => {
    handleMarkAsViewed(order.id);
    onSelectOrder(order);
  };

  const filteredOrders = filters
    ? orders.filter((order) => {
        if (boughtOrderIds.includes(order.id)) {
          return false;
        }

        if (removedOrderIds.includes(order.id)) {
          return false;
        }

        const orderAction:
          | "like"
          | "dislike"
          | "dislike-removed"
          | "report"
          | null = orderActions?.[order.id] ?? null;
        if (orderAction === "report" || orderAction === "dislike") {
          return false;
        }

        if (!filters.urgente && order.isUrgent) {
          return false;
        }

        if (!filters.naoUrgente && !order.isUrgent) {
          return false;
        }

        const isPresencial = order.serviceType === "Presencial";
        const isRemoto = order.serviceType === "Remoto";

        if (!filters.presencial && isPresencial) {
          return false;
        }

        if (!filters.remoto && isRemoto) {
          return false;
        }

        const hasNoUnlocks = order.unlockedCount === 0;
        const hasUnlocks = order.unlockedCount > 0;

        if (!filters.ninguemLiberou && hasNoUnlocks) {
          return false;
        }

        if (!filters.outrosLiberaram && hasUnlocks) {
          return false;
        }

        const isNotEvaluated = !orderAction || orderAction === null;
        const isLiked = orderAction === "like";
        // Note: 'dislike' is already filtered out above, so we only check 'dislike-removed' here
        const isDisliked = orderAction === "dislike-removed";

        if (!filters.naoAvaliados && isNotEvaluated) {
          return false;
        }

        if (!filters.gostei && isLiked) {
          return false;
        }

        if (!filters.naoGostei && isDisliked) {
          return false;
        }

        return true;
      })
    : orders.filter((order) => {
        if (boughtOrderIds.includes(order.id)) {
          return false;
        }

        if (removedOrderIds.includes(order.id)) {
          return false;
        }

        const orderAction = orderActions?.[order.id] ?? null;
        return orderAction !== "report" && orderAction !== "dislike";
      });

  // Function to sort orders based on sortOption
  const sortOrders = (ordersToSort: Order[]): Order[] => {
    const sorted = [...ordersToSort];

    switch (sortOption) {
      case "newest":
        return sorted.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

      case "remote":
        return sorted.sort((a, b) => Number(b.isRemote) - Number(a.isRemote));

      case "distance":
        return sorted.sort((a, b) => a.distance - b.distance);

      case "urgency":
        // Urgent first (higher urgency first)
        return sorted.sort((a, b) => Number(b.isUrgent) - Number(a.isUrgent));
      case "recommended":
        return sorted.sort(
          (a, b) => Number(b.isRecommended) - Number(a.isRecommended),
        );
      default:
        // Keep original order (recommended by algorithm)
        return sorted;
    }
  };

  // Separate orders by section
  const highlightedOrdersUnsorted = filteredOrders.filter(
    (order) => order.isHighlighted,
  );
  const recommendedOrdersUnsorted = filteredOrders.filter(
    (order) => order.isRecommended,
  );
  const otherOrdersUnsorted = filteredOrders.filter(
    (order) => !order.isHighlighted && !order.isRecommended,
  );

  // Apply sorting to each section independently
  const highlightedOrders = sortOrders(highlightedOrdersUnsorted);
  const recommendedOrders = sortOrders(recommendedOrdersUnsorted);
  const otherOrders = sortOrders(otherOrdersUnsorted);

  // Primeira seção preenchida, na ordem fixa express → para-voce → outros.
  const firstFilledSection: SectionType | null =
    highlightedOrders.length > 0
      ? "express"
      : recommendedOrders.length > 0
        ? "para-voce"
        : otherOrders.length > 0
          ? "outros"
          : null;

  // Reporta a primeira seção para o template colorir o pull-to-refresh.
  useEffect(() => {
    onFirstSectionChange?.(firstFilledSection);
  }, [firstFilledSection]);

  // Cores da última seção visível, usadas para o skeleton de scroll infinito
  // se misturar com a seção em que os novos itens serão anexados.
  const loadingMoreColors =
    otherOrders.length > 0
      ? { sectionColor: "#F0F2F5", rowColor: "#F9FAFB" }
      : recommendedOrders.length > 0
        ? { sectionColor: "#D1FAE7", rowColor: "#F9FFF9" }
        : { sectionColor: "#F3EBFF", rowColor: "#FAF5FF" };

  // Use TipCard placement hook to calculate where tips should appear
  const { getTipAtPosition } = useTipCardPlacement(
    tipCardConfigs,
    tipCardState,
    {
      destaque: highlightedOrders.length,
      para_voce: recommendedOrders.length,
      outros: otherOrders.length,
    },
  );

  // Handle retry for errors - must be defined before any conditional returns
  const handleRetry = useCallback(() => {
    setErrorType(null);
    setHasInitialLoad(false);
    if (userRadius) {
      setOrders([]);
      setMatchOrders([]);
      setLastOffersLength(0);
      setInfiniteScrollOffset(0);
      void handleGetOffers(authToken, userRadius);
      void handleGetMatchOffers();
    }
  }, [authToken, userRadius, handleGetOffers, handleGetMatchOffers]);

  // Section color mapping for TipCards
  const getSectionColor = (section: "destaque" | "para_voce" | "outros") => {
    const colorMap = {
      destaque: {
        background: "#7c3aed",
        iconBg: "#6d28d9",
        icon: "#ffffff",
      },
      para_voce: {
        background: "#059669",
        iconBg: "#047857",
        icon: "#ffffff",
      },
      outros: {
        background: "#374151",
        iconBg: "#1f2937",
        icon: "#ffffff",
      },
    };
    return colorMap[section];
  };

  /**
   * Planejamento de tooltips do CMS por seção.
   * Usa:
   * - is_first_in_section: força a primeira aparição no índice 0
   * - frequency_every_x_cards: define a frequência de repetição (em cards)
   * - priority: escolhe o tooltip quando há mais de um candidato no mesmo índice
   */
  type PlannedTooltip = {
    tooltip: ICmsTooltip;
    nextIndex: number;
    step: number | null;
  };

  const buildPlannedTooltips = (
    tooltips: ICmsTooltip[] | undefined,
    sectionLength: number,
  ): PlannedTooltip[] => {
    if (!tooltips || !tooltips.length || sectionLength === 0) return [];

    return tooltips.map((t) => {
      const freq =
        t.frequency_every_x_cards && t.frequency_every_x_cards > 0
          ? t.frequency_every_x_cards
          : null;

      let firstIndex: number;

      if (t.is_first_in_section) {
        // Sempre tenta aparecer antes do primeiro card
        firstIndex = 0;
      } else if (freq !== null) {
        // Sem first_in_section: primeira aparição depois de N cards
        firstIndex = Math.max(0, freq - 1);
      } else {
        // Sem frequência e não first_in_section: não agenda automaticamente
        firstIndex = Number.POSITIVE_INFINITY;
      }

      return {
        tooltip: t,
        nextIndex: firstIndex,
        step: freq,
      };
    });
  };

  const pickTooltipAtIndex = (
    plan: PlannedTooltip[],
    index: number,
  ): ICmsTooltip | null => {
    if (!plan.length) return null;

    const candidates = plan.filter((p) => p.nextIndex === index);
    if (!candidates.length) return null;

    // Escolhe o melhor candidato:
    // 1) is_first_in_section = true primeiro
    // 2) priority (maior primeiro)
    // 3) id (menor primeiro)
    let chosen = candidates[0];

    for (let i = 1; i < candidates.length; i++) {
      const c = candidates[i];

      if (
        c.tooltip.is_first_in_section !== chosen.tooltip.is_first_in_section
      ) {
        if (c.tooltip.is_first_in_section) {
          chosen = c;
        }
        continue;
      }

      if (c.tooltip.priority !== chosen.tooltip.priority) {
        if (c.tooltip.priority > chosen.tooltip.priority) {
          chosen = c;
        }
        continue;
      }

      if (c.tooltip.id < chosen.tooltip.id) {
        chosen = c;
      }
    }

    // Atualiza o próximo índice do tooltip escolhido
    const planItem = plan.find((p) => p.tooltip.id === chosen.tooltip.id);
    if (planItem) {
      if (planItem.step && planItem.step > 0) {
        planItem.nextIndex += planItem.step;
      } else {
        planItem.nextIndex = Number.POSITIVE_INFINITY;
      }
    }

    return chosen.tooltip;
  };

  const renderCmsTooltipRow = (
    section: "express" | "para-voce" | "outros",
    tooltip: ICmsTooltip,
    key: string,
  ) => {
    const content =
      tooltip.title && tooltip.title.trim().length > 0
        ? `${tooltip.title}\n\n${tooltip.text}`
        : tooltip.text;

    const iconColor =
      section === "express"
        ? "#6b34bf"
        : section === "para-voce"
          ? "#059669"
          : "#6b7280";

    const backgroundColor =
      section === "express"
        ? "#F3EBFF"
        : section === "para-voce"
          ? "#D1FAE7"
          : "#F0F2F5";

    return (
      <div
        key={key}
        style={{
          backgroundColor,
          paddingLeft: "16px",
          paddingRight: "20px",
          paddingTop: "8px",
          paddingBottom: "8px",
          borderLeft: "1px solid var(--neutral-200)",
          borderRight: "1px solid var(--neutral-200)",
        }}
      >
        <SectionTooltip
          content={content}
          disabled={ordersDisabled}
          color={iconColor}
        />
      </div>
    );
  };

  // Reset sticky section when view mode changes
  useEffect(() => {
    setCurrentSection(null);
    if (onCurrentSectionChange) {
      onCurrentSectionChange(null);
    }
  }, [viewMode, onCurrentSectionChange]);
  useEffect(() => {
    const handleScroll = () => {
      if (isLoadingMore || lastOffersLength < 29) return;

      const now = Date.now();
      if (now - lastScrollTimeRef.current < 2000) return;

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom < 500) {
        lastScrollTimeRef.current = now;
        const newOffset = infiniteScrollOffset + 30;
        setInfiniteScrollOffset(newOffset);
        void handleGetOffers(authToken, userRadius || 70, newOffset);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    isLoadingMore,
    lastOffersLength,
    infiniteScrollOffset,
    authToken,
    userRadius,
    handleGetOffers,
  ]);

  // Unified scroll detection for sticky header (SAME logic for both Classic and Compact views)
  useEffect(() => {
    const handleScroll = () => {
      // HARD GUARD 1 (Compact view only): Force hidden when at top of page
      const currentScrollY = window.scrollY || window.pageYOffset;
      if (viewMode === "list" && currentScrollY === 0) {
        if (currentSection !== null) {
          setCurrentSection(null);
          if (onCurrentSectionChange) {
            onCurrentSectionChange(null);
          }
        }
        return;
      }

      // Build list of section headers with their refs
      const sectionHeaders = [
        {
          headerRef: expressHeaderRef,
          type: "express" as SectionType,
          orders: highlightedOrders,
        },
        {
          headerRef: paraVoceHeaderRef,
          type: "para-voce" as SectionType,
          orders: recommendedOrders,
        },
        {
          headerRef: outrosHeaderRef,
          type: "outros" as SectionType,
          orders: otherOrders,
        },
      ].filter((s) => s.orders.length > 0); // Only check sections that have orders

      if (sectionHeaders.length === 0) {
        setCurrentSection(null);
        if (onCurrentSectionChange) {
          onCurrentSectionChange(null);
        }
        return;
      }

      // The sticky header sits at the bottom of the topbar
      const stickyPosition = topBarHeight;

      // HARD GUARD 2 (Compact view only): Force hidden when first section is fully visible
      if (
        viewMode === "list" &&
        sectionHeaders.length > 0 &&
        sectionHeaders[0].headerRef.current
      ) {
        const firstRect =
          sectionHeaders[0].headerRef.current.getBoundingClientRect();
        // If the top edge of the first section header is at or below the sticky position,
        // it means the header is fully visible, so sticky must be hidden
        if (firstRect.top >= stickyPosition) {
          if (currentSection !== null) {
            setCurrentSection(null);
            if (onCurrentSectionChange) {
              onCurrentSectionChange(null);
            }
          }
          return;
        }
      }

      let activeSection: SectionType | null = null;

      // UNIFIED LOGIC (same for both Classic and Compact):
      // Find the LAST section whose header has scrolled into or past the sticky position
      for (let i = 0; i < sectionHeaders.length; i++) {
        const section = sectionHeaders[i];
        if (section.headerRef.current) {
          const rect = section.headerRef.current.getBoundingClientRect();

          // If header's top edge is BELOW the sticky position, it's fully visible
          // Skip this section - it should NOT activate sticky
          if (rect.top >= stickyPosition) {
            continue;
          }

          // If we reach here, the header's top is ABOVE the sticky position
          // This means it's being covered or has already passed

          // Case 1: Header is PARTIALLY covered (bottom edge still below sticky position)
          if (rect.bottom > stickyPosition) {
            activeSection = section.type;
            // Don't break - continue checking for later sections
          }
          // Case 2: Header has FULLY passed (both top and bottom above sticky position)
          else if (rect.bottom <= stickyPosition) {
            activeSection = section.type;
            // This section stays active until the next one becomes active
          }
        }
      }

      // Update current section if changed
      if (activeSection !== currentSection) {
        setCurrentSection(activeSection);
        if (onCurrentSectionChange) {
          onCurrentSectionChange(activeSection);
        }
      }
    };

    // Initial check with delay to ensure DOM is ready
    const timer = setTimeout(handleScroll, 100);

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    viewMode,
    highlightedOrders.length,
    recommendedOrders.length,
    otherOrders.length,
    topBarHeight,
    currentSection,
    onCurrentSectionChange,
  ]);

  // Check if we should show loading state
  const shouldShowLoading = loadingOffers || loadingMatchOffers;

  // Check if we should show error screen
  const hasError = errorType !== null;
  const isEmpty = hasInitialLoad && filteredOrders.length === 0 && !hasError;

  // If loading state should be shown
  if (shouldShowLoading) {
    return <OrderFeedSkeleton topBarHeight={topBarHeight} />;
  }

  // Show error screen if there's an error
  if (hasError) {
    return (
      <ErrorScreen
        errorType={errorType}
        onRetry={handleRetry}
        onClearFilters={onClearFilters}
        topBarHeight={topBarHeight}
      />
    );
  }

  // Show empty list screen if no orders after filtering
  if (isEmpty) {
    return (
      <ErrorScreen
        errorType="empty-list"
        onRetry={handleRetry}
        onClearFilters={onClearFilters}
        topBarHeight={topBarHeight}
      />
    );
  }

  // Section colors for list view
  const sectionColors = {
    highlighted: "#a855f7", // Purple
    recommended: "#10b981", // Green
    other: "#6b7280", // Gray
  };

  const shouldHavePaddingTopOnOtherOrders =
    !highlightedOrders.length && !recommendedOrders.length;
  const shouldHavePaddingTopOnRecommendedOrders = !highlightedOrders.length;

  // Planeja tooltips do CMS apenas para o modo lista
  const expressTooltipsPlan =
    viewMode === "list"
      ? buildPlannedTooltips(
          cmsTooltipsBySection?.["express"],
          highlightedOrders.length,
        )
      : [];

  const paraVoceTooltipsPlan =
    viewMode === "list"
      ? buildPlannedTooltips(
          cmsTooltipsBySection?.["para-voce"],
          recommendedOrders.length,
        )
      : [];

  const outrosTooltipsPlan =
    viewMode === "list"
      ? buildPlannedTooltips(
          cmsTooltipsBySection?.["outros"],
          otherOrders.length,
        )
      : [];

  // If list view
  if (viewMode === "list") {
    return (
      <>
        <div
          style={{
            backgroundColor: "white",
          }}
        >
          {/* Highlighted Orders Section (List View) */}
          {highlightedOrders.length > 0 && (
            <div ref={expressRef}>
              <div
                ref={expressHeaderRef}
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  paddingTop: `calc(${topBarHeight}px + 14px)`, // Dynamic topbar height + balanced top padding
                  paddingBottom: "14px",
                  backgroundColor: "#F3EBFF",
                }}
              >
                <SectionHeader type="express" disabled={ordersDisabled} />
              </div>
              {/* Wrapper with padding and section background color */}
              {/*
                Se existir um TipCard educacional na posição 0 desta seção,
                o primeiro card NÃO deve ter o canto superior arredondado.
                Após o usuário dispensar o tip, o cálculo de placements
                deixa de colocá-lo na posição 0 e o primeiro card passa a
                receber a borda normalmente.
              */}
              <div
                style={{
                  backgroundColor: "#F3EBFF",
                  paddingLeft: "16px",
                }}
              >
                {/*
                  Flag estável para esta renderização, para saber se há
                  TipCard na primeira posição da seção.
                */}
                {(() => {
                  const hasTipAtFirstPosition =
                    !ordersDisabled && !!getTipAtPosition("destaque", 0);

                  return highlightedOrders.map((order, index) => {
                    const elements: ReactNode[] = [];
                    // Tooltip do CMS nesta posição, se existir
                    if (!ordersDisabled) {
                      const tooltip = pickTooltipAtIndex(
                        expressTooltipsPlan,
                        index,
                      );
                      if (tooltip) {
                        elements.push(
                          renderCmsTooltipRow(
                            "express",
                            tooltip,
                            `cms-tooltip-express-${tooltip.id}-${index}`,
                          ),
                        );
                      }
                    }

                    // TipCard educacional (TipCardConfig / tipcardsData) nesta posição
                    const tipPlacement = getTipAtPosition("destaque", index);
                    if (tipPlacement && !ordersDisabled) {
                      const colors = getSectionColor(tipPlacement.section);
                      elements.push(
                        <UnifiedTipCard
                          key={`tip-${tipPlacement.tipId}`}
                          config={{
                            ...tipPlacement.config,
                            backgroundColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.background
                                : tipPlacement.config.backgroundColorToken,
                            iconBgColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.iconBg
                                : tipPlacement.config.iconBgColorToken,
                            iconColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.icon
                                : tipPlacement.config.iconColorToken,
                          }}
                          variant="list"
                          onDismiss={onDismissTip}
                          isFirstInSection={index === 0}
                        />,
                      );
                    }

                    // Primeiro card só tem borda arredondada se NÃO houver tip na posição 0
                    const isOrderFirstInSection =
                      index === 0 && !hasTipAtFirstPosition;

                    elements.push(
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.25,
                          delay: Math.min(index, 6) * 0.05,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <OrderListItem
                          order={order}
                          onClick={() => handleSelectOrder(order)}
                          onMarkAsViewed={handleMarkAsViewed}
                          sectionColor="#a855f7"
                          orderAction={orderActions[order.id] || null}
                          disabled={ordersDisabled}
                          isFirstInSection={isOrderFirstInSection}
                          section="express"
                        />
                      </motion.div>,
                    );

                    return elements;
                  });
                })()}
              </div>
            </div>
          )}

          {/* Divider */}
          {highlightedOrders.length > 0 && recommendedOrders.length > 0 && (
            <div
              className="w-full border-t"
              style={{ borderColor: "var(--border)" }}
            ></div>
          )}

          {/* Recommended For You Section */}
          {recommendedOrders.length > 0 && (
            <div ref={paraVoceRef}>
              <div
                ref={paraVoceHeaderRef}
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  paddingTop: shouldHavePaddingTopOnRecommendedOrders
                    ? `calc(${topBarHeight}px + 14px)`
                    : "14px",
                  paddingBottom: "14px",
                  backgroundColor: "#D1FAE7",
                }}
              >
                <SectionHeader type="para-voce" disabled={ordersDisabled} />
              </div>
              {/* Wrapper with padding and section background color */}
              <div
                style={{
                  backgroundColor: "#D1FAE7",
                  paddingLeft: "16px",
                }}
              >
                {(() => {
                  const hasTipAtFirstPosition =
                    !ordersDisabled && !!getTipAtPosition("para_voce", 0);

                  return recommendedOrders.map((order, index) => {
                    const elements: React.ReactNode[] = [];

                    // Tooltip do CMS nesta posição, se existir
                    if (!ordersDisabled) {
                      const tooltip = pickTooltipAtIndex(
                        paraVoceTooltipsPlan,
                        index,
                      );
                      if (tooltip) {
                        elements.push(
                          renderCmsTooltipRow(
                            "para-voce",
                            tooltip,
                            `cms-tooltip-para-voce-${tooltip.id}-${index}`,
                          ),
                        );
                      }
                    }

                    // TipCard educacional (TipCardConfig / tipcardsData) nesta posição
                    const tipPlacement = getTipAtPosition("para_voce", index);
                    if (tipPlacement && !ordersDisabled) {
                      const colors = getSectionColor(tipPlacement.section);
                      elements.push(
                        <UnifiedTipCard
                          key={`tip-${tipPlacement.tipId}`}
                          config={{
                            ...tipPlacement.config,
                            backgroundColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.background
                                : tipPlacement.config.backgroundColorToken,
                            iconBgColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.iconBg
                                : tipPlacement.config.iconBgColorToken,
                            iconColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.icon
                                : tipPlacement.config.iconColorToken,
                          }}
                          variant="list"
                          onDismiss={onDismissTip}
                          isFirstInSection={index === 0}
                        />,
                      );
                    }

                    const isOrderFirstInSection =
                      index === 0 && !hasTipAtFirstPosition;

                    elements.push(
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.25,
                          delay: Math.min(index, 6) * 0.05,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <OrderListItem
                          order={order}
                          onClick={() => handleSelectOrder(order)}
                          onMarkAsViewed={handleMarkAsViewed}
                          sectionColor="#10b981"
                          orderAction={orderActions[order.id] || null}
                          disabled={ordersDisabled}
                          isFirstInSection={isOrderFirstInSection}
                          section="para-voce"
                        />
                      </motion.div>,
                    );

                    return elements;
                  });
                })()}
              </div>
            </div>
          )}

          {/* Divider */}
          {highlightedOrders.length > 0 ||
            (recommendedOrders.length > 0 && otherOrders.length > 0 && (
              <div
                className="w-full border-t"
                style={{ borderColor: "var(--border)" }}
              ></div>
            ))}

          {/* Other Orders Section */}
          {otherOrders.length > 0 && (
            <div ref={outrosRef}>
              <div
                ref={outrosHeaderRef}
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  paddingTop: shouldHavePaddingTopOnOtherOrders
                    ? `calc(${topBarHeight}px + 14px)`
                    : "14px",
                  paddingBottom: "14px",
                  backgroundColor: "#F0F2F5",
                  borderTop: "1px solid var(--border)",
                }}
              >
                <SectionHeader type="outros" disabled={ordersDisabled} />
              </div>
              {/* Wrapper with padding and section background color */}
              <div
                style={{
                  backgroundColor: "#F0F2F5",
                  paddingLeft: "16px",
                }}
              >
                {(() => {
                  const hasTipAtFirstPosition =
                    !ordersDisabled && !!getTipAtPosition("outros", 0);

                  return otherOrders.map((order, index) => {
                    const elements: React.ReactNode[] = [];

                    // Tooltip do CMS nesta posição, se existir
                    if (!ordersDisabled) {
                      const tooltip = pickTooltipAtIndex(
                        outrosTooltipsPlan,
                        index,
                      );
                      if (tooltip) {
                        elements.push(
                          renderCmsTooltipRow(
                            "outros",
                            tooltip,
                            `cms-tooltip-outros-${tooltip.id}-${index}`,
                          ),
                        );
                      }
                    }

                    // TipCard educacional (TipCardConfig / tipcardsData) nesta posição
                    const tipPlacement = getTipAtPosition("outros", index);
                    if (tipPlacement && !ordersDisabled) {
                      const colors = getSectionColor(tipPlacement.section);
                      elements.push(
                        <UnifiedTipCard
                          key={`tip-${tipPlacement.tipId}`}
                          config={{
                            ...tipPlacement.config,
                            backgroundColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.background
                                : tipPlacement.config.backgroundColorToken,
                            iconBgColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.iconBg
                                : tipPlacement.config.iconBgColorToken,
                            iconColorToken:
                              tipPlacement.config.type === "standard"
                                ? colors.icon
                                : tipPlacement.config.iconColorToken,
                          }}
                          variant="list"
                          onDismiss={onDismissTip}
                          isFirstInSection={index === 0}
                        />,
                      );
                    }

                    const isOrderFirstInSection =
                      index === 0 && !hasTipAtFirstPosition;

                    elements.push(
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.25,
                          delay: Math.min(index, 6) * 0.05,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <OrderListItem
                          order={order}
                          onClick={() => handleSelectOrder(order)}
                          onMarkAsViewed={handleMarkAsViewed}
                          sectionColor="#6b7280"
                          orderAction={orderActions[order.id] || null}
                          disabled={ordersDisabled}
                          isFirstInSection={isOrderFirstInSection}
                          section="outros"
                        />
                      </motion.div>,
                    );

                    return elements;
                  });
                })()}
              </div>
            </div>
          )}
          {isLoadingMore && (
            <LoadingMoreSkeleton
              count={3}
              sectionColor={loadingMoreColors.sectionColor}
              rowColor={loadingMoreColors.rowColor}
            />
          )}
        </div>
      </>
    );
  }

  // Cards view (default)
  // return (
  //   <div
  //     className="p-[0px]"
  //     style={{
  //       backgroundColor: 'var(--background)',
  //       paddingTop: `${topBarHeight}px`, // Dynamic topbar height - NO extra spacing
  //     }}
  //   >
  //     {/* Featured Orders Section */}
  //     {highlightedOrders.length > 0 && (
  //       <motion.section
  //         initial={{ opacity: 0, y: 20 }}
  //         animate={{ opacity: 1, y: 0 }}
  //         transition={{ duration: 0.4 }}
  //         className="pt-2 pb-2 px-2"
  //         style={{ backgroundColor: 'var(--background)' }}
  //       >
  //         <div
  //           style={{
  //             backgroundColor: '#f3ebff',
  //             borderRadius: '16px',
  //             padding: '12px',
  //           }}
  //         >
  //           {/* Section Header */}
  //           <div
  //             ref={expressHeaderRef}
  //             style={{ padding: '8px', marginBottom: '12px' }}
  //           >
  //             <SectionHeader type="express" disabled={ordersDisabled} />
  //           </div>

  //           {/* Cards */}
  //           <div className="space-y-3">
  //             {highlightedOrders.map((order, index) => {
  //               const elements: React.ReactNode[] = [];

  //               // Check for TipCard at this position using dynamic placement
  //               const tipPlacement = getTipAtPosition('destaque', index);
  //               if (tipPlacement && !ordersDisabled) {
  //                 const colors = getSectionColor(tipPlacement.section);
  //                 elements.push(
  //                   <UnifiedTipCard
  //                     key={`tip-${tipPlacement.tipId}`}
  //                     config={{
  //                       ...tipPlacement.config,
  //                       backgroundColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.background
  //                           : tipPlacement.config.backgroundColorToken,
  //                       iconBgColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.iconBg
  //                           : tipPlacement.config.iconBgColorToken,
  //                       iconColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.icon
  //                           : tipPlacement.config.iconColorToken,
  //                     }}
  //                     variant="card"
  //                     onDismiss={onDismissTip}
  //                   />
  //                 );
  //               }

  //               // Add the order card
  //               elements.push(
  //                 <motion.div
  //                   key={order.id}
  //                   initial={{ opacity: 0, x: -20 }}
  //                   animate={{ opacity: 1, x: 0 }}
  //                   transition={{ delay: index * 0.1 }}
  //                 >
  //                   <OrderCard
  //                     order={order}
  //                     onClick={() => handleSelectOrder(order)}
  //                     onMarkAsViewed={handleMarkAsViewed}
  //                     orderAction={orderActions[order.id] || null}
  //                     disabled={ordersDisabled}
  //                     section="express"
  //                   />
  //                 </motion.div>
  //               );

  //               return elements;
  //             })}
  //           </div>
  //         </div>
  //       </motion.section>
  //     )}

  //     {/* Divider - removed in cards view */}
  //     {viewMode === 'list' &&
  //       highlightedOrders.length > 0 &&
  //       recommendedOrders.length > 0 && (
  //         <div
  //           className="w-full border-t"
  //           style={{ borderColor: 'var(--border)' }}
  //         ></div>
  //       )}

  //     {/* Recommended For You Section */}
  //     {recommendedOrders.length > 0 && (
  //       <motion.section
  //         initial={{ opacity: 0, y: 20 }}
  //         animate={{ opacity: 1, y: 0 }}
  //         transition={{ duration: 0.4, delay: 0.2 }}
  //         className="pt-0 pb-2 px-2"
  //         style={{ backgroundColor: 'var(--background)' }}
  //       >
  //         <div
  //           style={{
  //             backgroundColor: viewMode === 'cards' ? '#D1FAE5' : 'transparent',
  //             borderRadius: viewMode === 'cards' ? '16px' : '0',
  //             padding: viewMode === 'cards' ? '12px' : '0',
  //           }}
  //         >
  //           {/* Section Header */}
  //           <div
  //             ref={paraVoceHeaderRef}
  //             style={{
  //               padding: viewMode === 'cards' ? '8px' : '4px',
  //               marginBottom: viewMode === 'cards' ? '12px' : '20px',
  //             }}
  //           >
  //             <SectionHeader type="para-voce" disabled={ordersDisabled} />
  //           </div>
  //           <div className={viewMode === 'cards' ? 'space-y-3' : ''}>
  //             {recommendedOrders.map((order, index) => {
  //               const elements: React.ReactNode[] = [];

  //               // Check for TipCard at this position using dynamic placement
  //               const tipPlacement = getTipAtPosition('para_voce', index);
  //               if (tipPlacement && !ordersDisabled) {
  //                 const colors = getSectionColor(tipPlacement.section);
  //                 elements.push(
  //                   <UnifiedTipCard
  //                     key={`tip-${tipPlacement.tipId}`}
  //                     config={{
  //                       ...tipPlacement.config,
  //                       backgroundColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.background
  //                           : tipPlacement.config.backgroundColorToken,
  //                       iconBgColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.iconBg
  //                           : tipPlacement.config.iconBgColorToken,
  //                       iconColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.icon
  //                           : tipPlacement.config.iconColorToken,
  //                     }}
  //                     variant={viewMode === 'cards' ? 'card' : 'list'}
  //                     onDismiss={onDismissTip}
  //                   />
  //                 );
  //               }

  //               // Add the order card
  //               elements.push(
  //                 <motion.div
  //                   key={order.id}
  //                   initial={{ opacity: 0, x: -20 }}
  //                   animate={{ opacity: 1, x: 0 }}
  //                   transition={{ delay: index * 0.1 }}
  //                 >
  //                   {viewMode === 'cards' ? (
  //                     <OrderCard
  //                       order={order}
  //                       onClick={() => handleSelectOrder(order)}
  //                       onMarkAsViewed={handleMarkAsViewed}
  //                       orderAction={orderActions[order.id] || null}
  //                       disabled={ordersDisabled}
  //                       section="para-voce"
  //                     />
  //                   ) : (
  //                     <OrderListItem
  //                       order={order}
  //                       onClick={() => handleSelectOrder(order)}
  //                       onMarkAsViewed={handleMarkAsViewed}
  //                       sectionColor="#059669"
  //                       orderAction={orderActions[order.id] || null}
  //                       disabled={ordersDisabled}
  //                       isFirstInSection={index === 0}
  //                     />
  //                   )}
  //                 </motion.div>
  //               );

  //               return elements;
  //             })}
  //           </div>
  //         </div>
  //       </motion.section>
  //     )}

  //     {/* Divider - removed in cards view */}
  //     {viewMode === 'list' &&
  //       (highlightedOrders.length > 0 || recommendedOrders.length > 0) &&
  //       otherOrders.length > 0 && (
  //         <div
  //           className="w-full border-t"
  //           style={{ borderColor: 'var(--border)' }}
  //         ></div>
  //       )}

  //     {/* Other Orders Section */}
  //     {otherOrders.length > 0 && (
  //       <motion.section
  //         initial={{ opacity: 0, y: 20 }}
  //         animate={{ opacity: 1, y: 0 }}
  //         transition={{ duration: 0.4, delay: 0.4 }}
  //         className="pt-0 pb-5 px-2"
  //         style={{ backgroundColor: 'var(--background)' }}
  //       >
  //         <div
  //           style={{
  //             backgroundColor: '#F0F2F5',
  //             borderRadius: '16px',
  //             padding: '12px',
  //           }}
  //         >
  //           {/* Section Header */}
  //           <div
  //             ref={outrosHeaderRef}
  //             style={{ padding: '8px', marginBottom: '12px' }}
  //           >
  //             <SectionHeader type="outros" disabled={ordersDisabled} />
  //           </div>

  //           {/* Cards */}
  //           <div className="space-y-3">
  //             {otherOrders.map((order, index) => {
  //               const elements: React.ReactNode[] = [];

  //               // Check for TipCard at this position using dynamic placement
  //               const tipPlacement = getTipAtPosition('outros', index);
  //               if (tipPlacement && !ordersDisabled) {
  //                 const colors = getSectionColor(tipPlacement.section);
  //                 elements.push(
  //                   <UnifiedTipCard
  //                     key={`tip-${tipPlacement.tipId}`}
  //                     config={{
  //                       ...tipPlacement.config,
  //                       backgroundColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.background
  //                           : tipPlacement.config.backgroundColorToken,
  //                       iconBgColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.iconBg
  //                           : tipPlacement.config.iconBgColorToken,
  //                       iconColorToken:
  //                         tipPlacement.config.type === 'standard'
  //                           ? colors.icon
  //                           : tipPlacement.config.iconColorToken,
  //                     }}
  //                     variant="card"
  //                     onDismiss={onDismissTip}
  //                   />
  //                 );
  //               }

  //               // Add the order card
  //               elements.push(
  //                 <motion.div
  //                   key={order.id}
  //                   initial={{ opacity: 0, x: -20 }}
  //                   animate={{ opacity: 1, x: 0 }}
  //                   transition={{ delay: index * 0.1 }}
  //                 >
  //                   <OrderCard
  //                     order={order}
  //                     onClick={() => handleSelectOrder(order)}
  //                     onMarkAsViewed={handleMarkAsViewed}
  //                     orderAction={orderActions[order.id] || null}
  //                     disabled={ordersDisabled}
  //                     section="outros"
  //                   />
  //                 </motion.div>
  //               );

  //               return elements;
  //             })}
  //           </div>
  //         </div>
  //       </motion.section>
  //     )}
  //   </div>
  // );
}
